import {
  FOLDERS_TAB_KEY,
  FILTERS_TAB_KEY,
  DEFAULT_FILTER_KEY,
  DEFAULT_FOLDER_KEY,
  TREE_LEFT_PADDING,
} from "@/constants";
import { LocalStorageKeys } from "@/enums/LocalStorageKeys";
import { IPanelScope } from "@/interfaces/IPanelScope";
import { CacheService } from "./cache.service";
import { FiltersService } from "./filters.service";
import { FoldersService } from "./folders.service";
import { ITreeItem } from "@/interfaces";
import { SearchService } from "./search.service";
import { getActiveItemFromUrl } from "@/utils";
import { mapModuleToUrlType, Module } from "@/types";
import { findNodeById } from "@/utils/common";

const treeService = () => {
  const collapse = (scope: IPanelScope): void => {
    const key = getLocalStorageKey(LocalStorageKeys.TREE_STATE_KEY, scope);
    localStorage.removeItem(key);
  };

  const isNodeOpen = (scope: IPanelScope, id: string): boolean => {
    const openNodes = getOpenedNodes(scope);
    return openNodes.includes(id);
  };

  const getOpenedNodes = (scope: IPanelScope): string[] => {
    const key = getLocalStorageKey(LocalStorageKeys.TREE_STATE_KEY, scope);
    const treeState = localStorage.getItem(key);

    const nodes = treeState?.split(",") || [];

    // Check for auto_filter[parent] in URL and add it to open nodes
    const searchParams = new URLSearchParams(window.location.search);
    const autoFilterParent = searchParams.get("auto_filter[parent]");

    if (autoFilterParent && !nodes.includes(autoFilterParent)) {
      nodes.push(autoFilterParent);
    }

    const cvParam = searchParams.get("cv");

    if (cvParam && cvParam.includes("/")) {
      // For cv values like "4395/version/1/___f_5/Maintenance", extract "4395/version/1"
      const parts = cvParam.split("/");
      if (parts.length >= 3) {
        // Take only the first 3 parts (4395/version/1)
        const folderPath = parts.slice(0, 3).join("/");
        !nodes.includes(folderPath) && nodes.push(folderPath);
      }
    }

    return nodes;
  };

  const expandTreeNode = (scope: IPanelScope, id: string) => {
    const openItems = getOpenedNodes(scope);
    openItems.push(id);
    setOpenedNodes(scope, openItems);
  };

  const setOpenedNodes = (scope: IPanelScope, nodes: string[]): void => {
    const key = getLocalStorageKey(LocalStorageKeys.TREE_STATE_KEY, scope);
    nodes.length
      ? localStorage.setItem(key, nodes.join(","))
      : localStorage.removeItem(key);
  };

  const setActiveNode = (scope: IPanelScope, node: any): void => {
    const key = getLocalStorageKey(
      LocalStorageKeys.ACTIVE_TREE_ITEM_KEY,
      scope,
    );
    node?.id === "_DEFAULT_" || !node
      ? localStorage.removeItem(key)
      : localStorage.setItem(key, JSON.stringify(node));
  };

  const getActiveNode = (scope: IPanelScope): any => {
    const key = getLocalStorageKey(
      LocalStorageKeys.ACTIVE_TREE_ITEM_KEY,
      scope,
    );
    const activeNodeFromLocalStorage = localStorage.getItem(key);

    return activeNodeFromLocalStorage
      ? JSON.parse(activeNodeFromLocalStorage)
      : { id: DEFAULT_FILTER_KEY };
  };

  const isTreeCollapsed = (scope: IPanelScope): boolean => {
    const key = getLocalStorageKey(LocalStorageKeys.TREE_STATE_KEY, scope);
    return !localStorage.getItem(key);
  };

  const getLocalStorageKey = (
    localStorageKeys: LocalStorageKeys,
    { userId, module, projectId, tab }: IPanelScope,
  ): string => {
    const pefix = `tree-${tab}-${userId}-${module}-${projectId}`;

    switch (localStorageKeys) {
      case LocalStorageKeys.ACTIVE_TREE_ITEM_KEY:
        return `${pefix}-active-filter`.toLowerCase();
      case LocalStorageKeys.TREE_STATE_KEY:
        return `${pefix}-state`.toLowerCase();
      case LocalStorageKeys.LAST_URL_KEY:
        return `${pefix}-lasturl`.toLowerCase();
      default:
        console.error("Local storage key not found");
        throw "Local storage key not found";
    }
  };

  const getModuleDisplayName = (module: string): string => {
    switch (module.toLowerCase()) {
      case "testset":
        return "Test Sets";
      case "requirement":
        return "Requirements";
      case "issue":
        return "Issues";
      case "test":
        return "Tests";
      default:
        return "";
    }
  };

  const scrollToSelected = (id: string = "activeFilter") => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const getSelectedTab = (scope: IPanelScope): string => {
    if (FoldersService.getActiveFolderFromUrl()) {
      return FOLDERS_TAB_KEY;
    }

    if (FiltersService.getActiveFilterIdFromUrl()) {
      return FILTERS_TAB_KEY;
    }
    return (
      CacheService.getItemFromPersistCache(
        scope,
        LocalStorageKeys.SELECTED_TAB_KEY,
      ) ?? FILTERS_TAB_KEY
    );
  };

  const isUrlValid = (module: Module, url: string): boolean => {
    const moduleUrlType = mapModuleToUrlType(module);
    return url.includes(`/${moduleUrlType}`);
  };

  const fetchOpenNodes = (scope: IPanelScope, items: ITreeItem[]): string[] => {
    const filterId = getActiveItemFromUrl(scope);
    const openNodes = SearchService.getParents(items, filterId);
    return [...TreeService.getOpenedNodes(scope), ...openNodes];
  };

  const setLastUrl = (scope: IPanelScope, url: string) => {
    const key = getLocalStorageKey(LocalStorageKeys.LAST_URL_KEY, scope);
    const module = scope.module;
    if (isUrlValid(module, url)) {
      url.includes(DEFAULT_FILTER_KEY)
        ? localStorage.removeItem(key)
        : localStorage.setItem(key, url);
    } else {
      console.error(`setLastUrl=>invalid url ${scope.module} ${url}`);
    }
  };

  const getLastUrl = (scope: IPanelScope): string | null => {
    const key = getLocalStorageKey(LocalStorageKeys.LAST_URL_KEY, scope);
    const url = localStorage.getItem(key);
    if (url && isUrlValid(scope.module, url)) return url;

    return scope.tab === FILTERS_TAB_KEY
      ? `?cv=${DEFAULT_FILTER_KEY}`
      : `?folder=${DEFAULT_FOLDER_KEY}`;
  };

  const getPaddingLeft = (serverObject: any): number => {
    // if it is a root folder without childern
    // or child leaf folder
    return !serverObject?.has_children ? TREE_LEFT_PADDING : 0;
  };

  const deleteTreeItemFromState = (
    items: ITreeItem[],
    deletedNode: ITreeItem,
  ) => {
    const ancestries = deletedNode.serverObject.ancestry?.split("/");
    const closestAncestry = ancestries?.pop();

    const ancestryNode = closestAncestry
      ? findNodeById(items, closestAncestry)
      : undefined;

    if (ancestryNode) {
      ancestryNode.children = ancestryNode.children.filter(
        (item) => item.id !== deletedNode.id,
      );

      if (!ancestryNode.children.length) {
        ancestryNode.serverObject.has_children = false;
        ancestryNode.extra.hasChildren = false;
        ancestryNode.extra.paddingLeft = getPaddingLeft(
          ancestryNode.serverObject,
        );
      }
    } else {
      const index = items.findIndex((f) => f.id === deletedNode.id);
      if (index !== -1) {
        items.splice(index, 1);
      }
    }
  };

  return {
    isNodeOpen,
    getOpenedNodes,
    setOpenedNodes,
    collapse,
    isTreeCollapsed,
    expandTreeNode,
    setActiveNode,
    getActiveNode,
    scrollToSelected,
    getModuleDisplayName,
    getPaddingLeft,
    getSelectedTab,
    fetchOpenNodes,
    setLastUrl,
    getLastUrl,
    deleteTreeItemFromState,
  };
};

export const TreeService = treeService();
